import { ButtonV3 } from '@jouzen/ecom-components';
import { cx } from 'class-variance-authority';
import {
  cloneElement,
  type JSX,
  type MouseEvent,
  type PropsWithChildren,
  type RefObject,
  useRef,
} from 'react';
import {
  type AriaModalOverlayProps,
  Overlay,
  useButton,
  useModalOverlay,
  useOverlayTrigger,
} from 'react-aria';
import {
  type OverlayTriggerState,
  useOverlayTriggerState,
} from 'react-stately';

import LandingPageHeroChicletCtaText from './LandingPageHeroChicletCtaText';
import type { LandingPageHeroChicletCtaProps } from './types';

export interface LandingPageHeroChicletModalDialogProps {
  readonly isOpen: boolean;
  readonly onToggle: () => void;
}

export interface LandingPageHeroChicletModalProps {
  readonly buttonRef: RefObject<HTMLButtonElement | null>;
  readonly cta: LandingPageHeroChicletCtaProps;
  readonly Dialog: (
    props: LandingPageHeroChicletModalDialogProps,
  ) => JSX.Element;
}

interface ModalTriggerProps {
  readonly buttonRef: RefObject<HTMLButtonElement | null>;
  readonly children: (state: OverlayTriggerState) => JSX.Element;
  readonly cta: LandingPageHeroChicletCtaProps;
}

export interface ModalProps extends AriaModalOverlayProps, PropsWithChildren {
  readonly state: OverlayTriggerState;
}

const Modal = ({ state, children, ...props }: ModalProps) => {
  const ref = useRef<HTMLDivElement>(null);
  const { modalProps, underlayProps } = useModalOverlay(props, state, ref);

  return (
    <Overlay>
      <div className="fixed inset-0 z-100" {...underlayProps}>
        <div {...modalProps} ref={ref}>
          {children}
        </div>
      </div>
    </Overlay>
  );
};

const ModalTrigger = ({
  buttonRef,
  children,
  cta,
  ...props
}: ModalTriggerProps) => {
  const state = useOverlayTriggerState(props);
  const { overlayProps, triggerProps } = useOverlayTrigger(
    { type: 'dialog' },
    state,
  );
  const { buttonProps } = useButton(triggerProps, buttonRef);

  return (
    <>
      <ButtonV3
        className={cx('leading-[0px]', cta.className)}
        data-cy="landing-page-hero-chiclet-cta"
        ref={buttonRef}
        variant="unset"
        {...buttonProps}
        onClick={(event: MouseEvent<HTMLButtonElement>) => {
          buttonProps.onClick?.(event);
          cta.onClick?.();
        }}
      >
        <LandingPageHeroChicletCtaText label={cta.label} />
      </ButtonV3>
      {state.isOpen && (
        <Modal {...props} state={state}>
          {cloneElement(children(state), overlayProps)}
        </Modal>
      )}
    </>
  );
};

const LandingPageHeroChicletModal = ({
  buttonRef,
  cta,
  Dialog,
}: LandingPageHeroChicletModalProps) => {
  return (
    <ModalTrigger buttonRef={buttonRef} cta={cta}>
      {(overlayState) => (
        <Dialog
          isOpen={overlayState.isOpen}
          onToggle={() => overlayState.toggle()}
        />
      )}
    </ModalTrigger>
  );
};

export default LandingPageHeroChicletModal;
